// client/src/hooks/useTeamBenchmarks.ts
//
// Hook dual-purpose:
//   - Para coaches: carga y guarda benchmarks de un team específico (teamId explícito)
//   - Para players: llamar con teamId=null lo deja en estado inicial (overrides: null)
//
// Para la detección automática del team del player, usar usePlayerTeamBenchmarks.

import { useState, useEffect, useCallback } from 'react';
import { supabase, SUPABASE_ENABLED } from '../lib/supabase';
import { TeamBenchmarkOverrides } from '../contexts/BenchmarkContext';

interface UseTeamBenchmarksResult {
  overrides: TeamBenchmarkOverrides | null;
  loading:   boolean;
  save:      (newOverrides: TeamBenchmarkOverrides) => Promise<boolean>;
}

export function useTeamBenchmarks(teamId: string | null): UseTeamBenchmarksResult {
  const [overrides, setOverrides] = useState<TeamBenchmarkOverrides | null>(null);
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    if (!teamId || !SUPABASE_ENABLED) { setOverrides(null); return; }
    let cancelled = false;
    setLoading(true);

    supabase
      .from('team_benchmarks')
      .select('overrides')
      .eq('team_id', teamId)
      .maybeSingle()
      .then(({ data }: { data: { overrides: TeamBenchmarkOverrides } | null }) => {
        if (!cancelled) {
          const ov = data?.overrides ?? null;
          setOverrides(ov && Object.keys(ov).length > 0 ? ov : null);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [teamId]);

  const save = useCallback(async (newOverrides: TeamBenchmarkOverrides): Promise<boolean> => {
    if (!teamId || !SUPABASE_ENABLED) return false;
    const { error } = await supabase
      .from('team_benchmarks')
      .upsert(
        { team_id: teamId, overrides: newOverrides, updated_at: new Date().toISOString() },
        { onConflict: 'team_id' },
      );
    if (!error) setOverrides(Object.keys(newOverrides).length > 0 ? newOverrides : null);
    return !error;
  }, [teamId]);

  return { overrides, loading, save };
}
